import { useState, useContext, useEffect } from 'react'
import { Radio, FormControlLabel, Switch } from '@mui/material'
import { 
  School, 
  Elderly, 
  Assignment, 
  Cake, 
  Bolt, 
  LocalOffer,
  KeyboardArrowDown,
  KeyboardArrowUp
} from '@mui/icons-material'
import WizardDataContext from '../../../store/WizardDataContext';

export default function ChooseDeals({ setDisableBtn }) {
  const wizardCtx = useContext(WizardDataContext);
  const { wizardData, updateData } = wizardCtx;

  const deals = [
    {
      id: 'student',
      name: 'Student Deal',
      icon: School,
      description: 'I want to create a coupon for high school and university students. Usually applicable to all students younger than 18 or in possession of a student card.'
    },
    {
      id: 'pensioner',
      name: 'Pensioner Deal',
      icon: Elderly,
      description: 'I want to create a coupon for pensioners. Usually applicable to seniors aged 65+ in possession of an ID document or driver\'s license.'
    },
    {
      id: 'signup',
      name: 'Sign-up Deal',
      icon: Assignment,
      description: 'I want to create a coupon for first-time buyers. Usually a 10-20% discount on their first order only.'
    },
    {
      id: 'birthday',
      name: 'Birthday Deal',
      icon: Cake,
      description: 'I want to create a coupon for those celebrating their birthdays. Usually a free item like a 2L Coca-Cola on any order over R300. Shared with users on their birthdays.'
    },
    {
      id: 'popin',
      name: 'Pop-In Deal',
      icon: Bolt,
      description: 'I want to create a coupon rewarding users for visiting a particular address. Ideal for promoting a new store. Shared with users via our in-app notifications and Rewards screen.'
    },
    {
      id: 'other',
      name: 'Other Deal',
      icon: LocalOffer,
      description: 'I want to create a custom coupon with customizable terms and conditions.'
    }
  ];

  const [selectedDeal, setSelectedDeal] = useState(wizardData.dealSelection.selectedDeal || '');
  const [expandedDeal, setExpandedDeal] = useState(null);
  const includeCoupon = wizardData.dealSelection?.includeCoupon ?? true;

  useEffect(() => {
    // Enable continue button only if a deal is selected
    setDisableBtn(!selectedDeal);
  }, [selectedDeal, setDisableBtn])

  useEffect(() => {
    // Restore selected deal from context when component mounts
    if (wizardData.dealSelection.selectedDeal) {
      setSelectedDeal(wizardData.dealSelection.selectedDeal);
    }
  }, [wizardData.dealSelection.selectedDeal])

  const handleDealSelect = (dealId) => {
    setSelectedDeal(dealId);
    setExpandedDeal(dealId);
    if (dealId === 'popin') {
      // Keep includeCoupon as-is (default handled by context)
      updateData('dealSelection', { selectedDeal: dealId });
    } else {
      // Reset includeCoupon for non-popin deals so stale false doesn't leak
      updateData('dealSelection', { selectedDeal: dealId, includeCoupon: true });
    }
    // Reset pop-in budget when switching away from pop-in
    if (dealId !== 'popin') {
      updateData('popinBudget', { visitors: null, price: null, popinRateId: null });
    }
    
    // Automatically set terms and instructions based on deal template
    const dealTemplates = {
      'student': {
        terms: [
          'Applicable to students only',
          'NOT applicable to already discounted products and services'
        ],
        instructions: [
          "Use ID or Driver's License for Age Verification",
          'Use Student ID card to verify enrolment status'
        ]
      },
      'pensioner': {
        terms: [
          'Applicable to pensioners only',
          'NOT applicable to already discounted products and services'
        ],
        instructions: [
          "Use ID or Driver's License for Age Verification"
        ]
      },
      'signup': {
        terms: [
          'Applicable to new customers only',
          'NOT applicable to already discounted products and services'
        ],
        instructions: [
          'Place your first order'
        ]
      },
      'birthday': {
        terms: [
          'Only available on your birthday',
          'NOT applicable to already discounted products and services'
        ],
        instructions: [
          "Use ID or Driver's License for Age Verification"
        ]
      },
      'other': {
        terms: [],
        instructions: []
      },
      'popin': {
        // Pop-In Deal doesn't have specific template requirements
        terms: [],
        instructions: []
      }
    };
    
    const template = dealTemplates[dealId];
    if (template) {
      updateData('terms', {
        terms: template.terms,
        instructions: template.instructions
      });
    }
  }

  const handleIncludeCouponToggle = (event) => {
    const checked = !!event.target.checked;
    updateData('dealSelection', { includeCoupon: checked });
  };

  const toggleExpand = (dealId) => {
    setExpandedDeal(expandedDeal === dealId ? null : dealId);
  }

  return (
    <>
      <div className="choose-deals-container">
        <div className="choose-deals-header">
          <h1>Choose Deals</h1>
        </div>

        <div className="deals-list">
          {deals.map((deal) => {
            const IconComponent = deal.icon;
            const isSelected = selectedDeal === deal.id;
            const isExpanded = expandedDeal === deal.id;

            return (
              <div 
                key={deal.id} 
                className={`deal-card ${isSelected ? 'selected' : ''}`}
                onClick={() => handleDealSelect(deal.id)}
              >
                <div className="deal-card-header">
                  <div className="deal-radio-icon">
                    <Radio
                      checked={isSelected}
                      onChange={() => handleDealSelect(deal.id)}
                      value={deal.id}
                      sx={{
                        color: '#c4c4ce',
                        '&.Mui-checked': {
                          color: '#0A71F5',
                        },
                      }}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <div className="deal-icon">
                      <IconComponent 
                        sx={{ 
                          fontSize: 28, 
                          color: '#666' 
                        }} 
                      />
                    </div>
                  </div>
                  <div className="deal-title">
                    <h3>{deal.name}</h3>
                  </div>
                  <div 
                    className="deal-expand-icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleExpand(deal.id);
                    }}
                  >
                    {isExpanded ? (
                      <KeyboardArrowUp sx={{ fontSize: 24, color: '#666' }} />
                    ) : (
                      <KeyboardArrowDown sx={{ fontSize: 24, color: '#666' }} />
                    )}
                  </div>
                </div>

                <div 
                  className={`deal-description ${isExpanded ? 'expanded' : ''}`}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="deal-description-content">
                    <p>{deal.description}</p>
                    {deal.id === 'popin' && isSelected && (
                      <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #e6e6ee' }}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={includeCoupon}
                              onChange={handleIncludeCouponToggle}
                              color="primary"
                            />
                          }
                          label="Include coupon for this deal"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  )
}
